/**
 * useDeferredFirstSend — mint a session on the FIRST message instead of
 * eagerly, then flush that message once a real session exists. Shared by
 * every composer that can start session-less: Session.tsx's `/sessions/new`
 * route, Home's agent FACE (chat and board), and each board wrapper's own
 * chat side-channel in face mode. One implementation — no parallel minting
 * path.
 *
 * Two call shapes, selected by whether `sessionId` is bound:
 *   - BOUND (a real session already exists): `send` calls `sendMessage`
 *     directly — a passthrough, no deferral.
 *   - UNBOUND (`sessionId` is null/undefined/''): `send` creates the session
 *     WITH the first message (useCreateSession — toast + rollback already
 *     owned by that hook; creating runs the session, so there is nothing to
 *     flush — the message IS the session's first) and calls `onCreated` so
 *     the caller can transition its own routing/selection (navigate(), or
 *     Home's face → conversation swap).
 *
 * The flush is a `useEffect` keyed on the `sessionId` THIS hook instance is
 * bound to — never on router state. That is the fix for the board-face
 * hazard: a board wrapper doesn't navigate on session creation (Home swaps
 * panes via store state, not a route change), so a flush trigger keyed on
 * the route would never fire for it. Every consumer — including Session.tsx,
 * which still navigates for URL correctness — drives the SAME trigger; the
 * flush never depends on that navigation completing, only on the sessionId
 * value the hook was given transitioning to (or starting at) a real id.
 * Exactly one flush trigger exists (this effect); the store's atomic
 * `takeIfMatches` (not a component-local ref) is what makes a StrictMode
 * double-invoke, or a second hook instance racing on the same id, safe.
 *
 * Failure: `useCreateSession` already surfaces the toast + rolls back the
 * optimistic list; this hook additionally restores the typed text into
 * useChatInputStore (the composer store every consumer here renders through)
 * so it isn't silently lost — nulling the pending ref and only logging (the
 * pre-extraction Session.tsx behaviour) meant the toast fired but the words
 * were gone.
 */
import { useCallback } from 'react';

import { useCreateSession } from './useSessions';
import { useChatInputStore } from '@/stores/chatInput';
import { randomUUID } from '@/lib/uuid';
import type { SessionResponse } from '@/api/types_session';

export interface DeferredSendOptions {
  /** Stable across retries of THIS message so a resend dedups server-side. */
  idempotencyKey?: string;
}

export interface UseDeferredFirstSendParams {
  /** The session this hook instance is bound to, or falsy when none exists
   *  yet — the sole flush trigger (see module doc). */
  sessionId: string | null | undefined;
  /** Identity for the deferred POST /sessions — read only while unbound. */
  agentSlug?: string;
  patientId?: string;
  /**
   * The real send once a session exists — e.g. `streaming.sendMessage`.
   * Optional: a caller that is provably always unbound for the lifetime of
   * this hook instance (Home's ChatFace — it unmounts the instant a session
   * exists, replaced by ChatConnected's own bound instance) never reaches the
   * bound branch, so it has no `sendMessage` to give.
   */
  sendMessage?: (content: string, options?: DeferredSendOptions) => Promise<void> | void;
  /** Fired once, right after a deferred create succeeds — the caller's hook
   *  to transition its own routing/selection. Never called on the bound path
   *  (a bound instance never creates). */
  onCreated?: (session: SessionResponse) => void;
}

export function useDeferredFirstSend({
  sessionId,
  agentSlug,
  patientId,
  sendMessage,
  onCreated,
}: UseDeferredFirstSendParams) {
  const createSession = useCreateSession();
  const setInput = useChatInputStore((s) => s.setValue);

  const send = useCallback(
    async (content: string, options?: DeferredSendOptions) => {
      if (sessionId) {
        if (!sendMessage) return; // unreachable: a bound caller always gives one
        await sendMessage(content, options);
        return;
      }
      // The catch below must only ever mean "the session was never created" —
      // setPending + onCreated run OUTSIDE the try so a throw from the
      // caller's onCreated (a pure state transition, not expected to throw,
      // but not this hook's to assume) can never be mistaken for a failed
      // create and restore text for a session that in fact already exists.
      let session;
      try {
        session = await createSession.mutateAsync({
          agentSlug: agentSlug ?? '',
          message: content,
          ...(patientId ? { subject: { type: 'patient', id: patientId } } : {}),
          idempotencyKey: randomUUID(),
        });
      } catch {
        // useCreateSession surfaced the toast + rolled back the optimistic
        // list; restore the typed text so it isn't lost — the message was
        // never durably sent.
        setInput(content);
        return;
      }
      // Nothing to flush: the create's first message IS this send.
      onCreated?.(session);
    },
    [sessionId, sendMessage, createSession, agentSlug, patientId, onCreated, setInput],
  );

  return { send, isPending: createSession.isPending };
}
